<script lang="ts">
  import FaArrowCircleLeft from "svelte-icons/fa/FaArrowCircleLeft.svelte";
  import FaArrowCircleRight from "svelte-icons/fa/FaArrowCircleRight.svelte";
  import FaShareAlt from "svelte-icons/fa/FaShareAlt.svelte";
  import FaRegCalendarAlt from "svelte-icons/fa/FaRegCalendarAlt.svelte";
  import FaAngleDown from 'svelte-icons/fa/FaAngleDown.svelte';
  import FaAngleUp from 'svelte-icons/fa/FaAngleUp.svelte';
  import FaRegTimesCircle from 'svelte-icons/fa/FaRegTimesCircle.svelte';
  import FaRegQuestionCircle from 'svelte-icons/fa/FaRegQuestionCircle.svelte';
  import { tooltip } from "svooltip";
  import Button from "../../ui-components/button.svelte";
  import FilterSelect from "../../ui-components/filter-select.svelte";
  import Loader from "../../ui-components/loader.svelte";
  import IconDropdown from "../../ui-components/icon-dropdown.svelte";
  import VisualizationContainer from "../../ui-components/visualization-container.svelte";
  import DatePicker from "../../ui-components/date-picker.svelte";
  import CustomError from "../../ui-components/custom-error.svelte";
  import { createValueLabel, getTimeSeriesName } from "../chart/labels";
  import type { FigureDataSourceInterface, FigureData } from "./figure-data-source-interface";
  import type {
    VisualizationData,
  } from "../data-source-interface";
  import { visualizationDataStore, showHeaderStore } from "../stores";
  import type { SelectorSelections } from "../types";
  import {
    DataValueType,
    DateFilter,
    Share
  } from "../options";
  import { 
    selectorSelectionsToSeriesOptions,
    generateUID,
    createQuery,
    sortByDisabledAndSelected,
    sortOptionsAlphabetically,
    updateProps,
    checkDateIncludedInArray,
    handleHeader,
  } from "../utils";
  import { onMount, type ComponentProps } from "svelte";
  import { fade } from "svelte/transition";

  import { t, locale } from "../../translations/i18n";

  export let dataSource: FigureDataSourceInterface;

  /** FilterSelect props in this collection are rendered into the selector */
  export let filterSelectProps: ComponentProps<FilterSelect>[] = [];

    /** Control when "Add data" & "Back" buttons are shown in the selector */
  export let showSelectorButtons: boolean;

  // If the view is being restored from a previous session, the data is loaded from the storage
  export let restoreView: boolean = false;
  export let storageData: any = [];

  const month_list = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]

  /** Default options for the UI */
  const options: any = {
      dataValueType: DataValueType.ABSOLUTE,
      dateFilter: [DateFilter.MONTHS],
    };
  
  /** Unique id for each visualization */
  let uniqueId: string = restoreView ? storageData.id : generateUID();

  /** Reload the filter select props when the filterSelectPropsChanged variable is changed. */
  let filterSelectPropsChanged: boolean = false;

  /** "Add data" button should be disabled until there is a selection on each field */
  let addDataButtonDisabled: boolean = true;

  let buttonsDisabled: boolean = true;
  
  /** Initial state for the loaders */
  let showContainerLoader: boolean = false;
  let showSelectorLoader: boolean = false;

  /** Selections made on the selector */
  let selectorSelections: SelectorSelections = { statName: "", statVerboseName: {}, is_daily: false, selections: {} };

  /** Control visibility for the date selector */
  let showDatePicker: boolean = false;

  /** Control for the aggregation mode */
  let aggregationMode: boolean = false;

  /** Control whether the latest datapoint is loaded */
  let latestDatapoint: boolean = false;

  /** Variable to identify whether previous value was aggregated */
  let previousValueAggregated: boolean = false;

  /** Aggregation mode variables */
  let aggregationFrom: Date;
  let aggregationTo: Date;
  let aggregationPicker: boolean = false;

  let aggregationDateTitle: string;

  /** Collection for metadata from figure */
  let metadata_array: any;

  /** Period selector button disable control in case of daily data */
  let isDaily: boolean = false;

  let currentContainer: any;

  /** Variable containing the current language of the page */
  let currentLanguage: any = $locale.split("-")[0]

  /** Set Figure Source */
  let source_text: string;
  
  /** Set visualization data default values */
  let visualizationData: VisualizationData = {
    id: uniqueId,
    type: "Figure",
    metadata: metadata_array,
    period: ["Months"],
    visualizationSize: "h-large-v-small",
  };

  let values: Array<number | null> = [];
  let changes: Array<number> = [];
  let units: Array<string> = []; 
  let description: string = "";
  let data_day: string | null = ""; // Needs to be empty or null if monthly data
  let data_month: string = "";
  let data_year: number | string = "";


  /**
   * Index of the current data in the data array
   */
  let current_data_index: number;

  /** Available dates for the date picker */
  let available_dates: Array<Date> = [];

  /** Text input content for the date picker */
  let text_input_date: string;

  /** Variable containing the current focused element index */
  let focusedElementIndex: number = 0;

  /** Showing an error message when the data is unavailable. */
  let error_message: boolean = false;

  /** Show additional error info on restoreDataHandler error */
  let restoreDataError: boolean = false;

  /** Functionality for hiding headers */
  let showHeader: any;
  showHeaderStore.subscribe(value => {
    showHeader = value;
  });
  $: handleHeader(showHeader, uniqueId);

  /**
   * "Back button" handler
   *
   * Clicking back should take the user to the previous step in the selector.
   * For example, clicking back from the fields should show the statistics again.
   *
   * TODO: Implement more steps in the selector
   */
   const backHandler = (): void => {
    resetSelector();
  };

  /**
   * Reset selector
   */
   const resetSelector = (): void => {
    addDataButtonDisabled = true;
    selectorSelections = { statName: "", statVerboseName: {}, is_daily: false, selections: {} };
    addStatistics();
  };

  /** 
   * Add statistics into the selector 
   */
  const addStatistics = async (): Promise<void> => {
    // Hide the "Add data" & "Back" buttons because clicking a stat takes the
    // user directly to the next "level"
    showSelectorButtons = false;
    showSelectorLoader = true;

    const statistics = await dataSource.getStatistics();
    // First, sort options alphabetically
    const sortedAlphabetically = sortOptionsAlphabetically(statistics.map((s) => ({
      model: s.model,
      name: s.name,
      title: s.description,
      keywords: s.keywords,
      disabled: s.disabled,
      is_daily: s.is_daily,
    })), true);

    // Then, sort the options by the disabled property
    sortedAlphabetically.sort((a: any, b: any) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));

    filterSelectProps = [
      {
        title: $t("Statistics"),
        options: sortedAlphabetically,
        visualizationId: `container-id-${uniqueId}`,
        selectCallback: async (_title, selected, _selectClicked) => {
          const statName = selected?.[0]?.model || "";
          const statVerboseName = selected?.[0]?.name || "";
          const is_daily = selected?.[0]?.is_daily || false;
          selectorSelections.statName = statName;
          selectorSelections.statVerboseName = statVerboseName;
          selectorSelections.is_daily = is_daily;
          await addFields(statName);
        },
      },
    ];

    showSelectorLoader = false;

  };

  /**
   * Add fields into the selector
   *
   * @param statName - Name of the statistic
   */
  const addFields = async (statName: string): Promise<void> => {

    showSelectorLoader = true;
    let updatedFields: any = [];

    const fields = await dataSource.getFields(statName);
    filterSelectProps = fields.map((field) => {
      return {
        name: field.name,
        field: field.field,
        title: field.name[currentLanguage],
        visualizationId: `container-id-${uniqueId}`,
        options: sortOptionsAlphabetically(field.options.map((option) => ({
          name: option.name,
          disabled: option.disabled,
          parent_country: option.parent_country,
        })), true),
        multiselect: false, // Figure can show only one data at a time, so multiselection is disabled
        selectCallback: async (title, selected, _selectClicked) => {
          const values = selected?.map((s) => s.name["en"]) || [];
          const fieldVerboseName = selected?.map((s) => s.name) || [];
          selectorSelections.selections[field.field] = {
            values: fieldVerboseName as Object[],
            values_db: values as string[],
            verbose_key_name: field.name,
          };

          // Preventing user to click unavailable combinations of fields
          showSelectorLoader = true;

          // Call the function to get the disabled fields based on selection.
          updatedFields = await dataSource.updateDisabledFields(
            selectorSelections.selections,
            statName
          );

          showSelectorLoader = false;

          // Update the props of the fields
          filterSelectPropsChanged = !filterSelectPropsChanged;
          updateProps(updatedFields, filterSelectProps);

          // Sort the options alphabetically
          sortOptionsAlphabetically(filterSelectProps);

          // Sort the fields by disabled and selected values
          sortByDisabledAndSelected(filterSelectProps);

          // "Add data" button enabled if all fields have at least one selection
          addDataButtonDisabled =
            Object.keys(selectorSelections.selections).filter(
              (key) => selectorSelections.selections[key]["values_db"].length > 0
            ).length != fields.length;
        },
      };
    });

    showSelectorButtons = true;
    showSelectorLoader = false;
  };


  /**
   * Function for getting the data from the API and displaying it in the figure container
   */
  const addFigureHandler = async (): Promise<void> => {
    try {
      showContainerLoader = true;

      // If any daily data added to the table, then the period selector will be disabled.
      if (selectorSelections.is_daily){
        isDaily = true;
      }

      let timestamps: Array<number> = [];
      const seriesOptions = selectorSelectionsToSeriesOptions(selectorSelections);

      // Create query from series options
      await Promise.all(
        seriesOptions.map(async (seriesOption) => {
          const query = createQuery(options, seriesOption);
          const data: FigureData = await dataSource.getFigureData(query);
          const last_date = new Date(data.dates[data.lastDatapointIndex]);
          let sources = data.source;
          // Text for data sources that will be displayed in the figure description
          source_text = Array.from(sources).filter((source) => source !== null && source !== "" && source !== undefined).join(", ");
          values = data.values;
          units = data.units;
          changes = data.changes;
          description = getTimeSeriesName(seriesOption, data.statName);
          timestamps = data.dates;
          data_month = $t(last_date.toLocaleString('en-US', { month: 'long' }));
          data_year = last_date.getFullYear();
          if (isDaily){
            data_day = last_date.getDate().toString();
          }

          current_data_index = data.lastDatapointIndex;
          text_input_date = `${last_date.getFullYear()}/${last_date.getMonth() + 1}/${last_date.getDate()}`;
        })
      );

      showContainerLoader = false;
      buttonsDisabled = false;

      timestamps.forEach((timestamp: number) => {
        available_dates.push(new Date(timestamp));
      });

      updateAggregationLabels();

      storeVisualizationData(selectorSelections);

      // Handle header depending on showHeader state
      handleHeader(showHeader, uniqueId);
    } catch (error) {
      error_message = true;
      console.error("Error in addFigureHandler: ", error);
    }
  };

  /**
   * Function for getting the data from the API and displaying it in the figure container
   */
   const restoreDataHandler = async (visualization: any, aggregation: boolean = false): Promise<void> => {
    try {
      showContainerLoader = true;

      // If any daily data added to the table, then the period selector will be disabled.
      if (selectorSelections.is_daily){
        isDaily = true;
      }

      let timestamps: Array<number> = [];
      let seriesOptions;
      let restored_dateFilter: DateFilter; 
      let restored_data_index: any;

      if (visualization.metadata) {
        seriesOptions = selectorSelectionsToSeriesOptions(visualization.metadata[0]);
      } else {
        seriesOptions = selectorSelectionsToSeriesOptions(selectorSelections);
      }

      if (options.dateFilter.includes("dataIndex")) {
        restored_dateFilter = options.dateFilter;
        restored_data_index = options.dateFilter[options.dateFilter.indexOf("dataIndex") + 1];
        if (options.dateFilter.length > 3 ) {
          aggregationFrom = new Date(options.dateFilter[options.dateFilter.indexOf("dataIndex") + 2]);
          aggregationTo = new Date(options.dateFilter[options.dateFilter.indexOf("dataIndex") + 3]);
          options.dateFilter.length = 2

          // Data needs to be fetched with "Months" as a date filter
          options.dateFilter = ["Months"]

          // Initial API call for getting dates
          await Promise.all(
            seriesOptions.map(async (seriesOption) => {
              const query = createQuery(options, seriesOption);
              const data: FigureData = await dataSource.getFigureData(query);
              let sources = data.source;
              source_text = Array.from(sources).filter((source) => source !== null && source !== "" && source !== undefined).join(", ");
              timestamps = data.dates;
            })
          );

          timestamps.forEach((timestamp: number) => {
            available_dates.push(new Date(timestamp));
          });

          options.dateFilter = restored_dateFilter
          // Get aggregated data
          getAggregatedData()
          buttonsDisabled = false;

          // Load a list of statistic, otherwise selector will not work
          await addStatistics();

          // Handle header depending on showHeader state
          handleHeader(showHeader, uniqueId);

          return
        }
      }
      // Data needs to be fetched with "Months" as a date filter
      options.dateFilter = ["Months"]

      // Create query from series options
      await Promise.all(
        seriesOptions.map(async (seriesOption) => {
          const query = createQuery(options, seriesOption);
          const data: FigureData = await dataSource.getFigureData(query);
          let date: Date;
          values= data.values;
          units = data.units;
          changes = data.changes;
          description = getTimeSeriesName(seriesOption, data.statName);
          timestamps = data.dates;
          let sources = data.source;
          source_text = Array.from(sources).filter((source) => source !== null && source !== "" && source !== undefined).join(", ");

          // Restoring same data that user have selected
          if (restored_data_index) {
            current_data_index = restored_data_index;
            options.dateFilter = restored_dateFilter;

            date = new Date(data.dates[restored_data_index]);
          } else {
            current_data_index = data.lastDatapointIndex;
            date = new Date(data.dates[data.lastDatapointIndex]);
          }

          data_month = $t(date.toLocaleString('en-US', { month: 'long' }));
          data_year = date.getFullYear();
          if (isDaily){
            data_day = date.getDate().toString();
            // Update the state of date in DatePicker
            text_input_date = `${data_year}/${date.getMonth() + 1}/${date.getDate()}`;
          } else {
            // Update the state of date in DatePicker
            text_input_date = `${data_year}/${date.getMonth() + 1}/${1}`;
          }

          if (aggregation) {
            current_data_index = timestamps.findIndex((d) => new Date(d).getFullYear() == aggregationFrom.getFullYear() && new Date(d).getMonth() == aggregationFrom.getMonth());
          }
        })
      );

      timestamps.forEach((timestamp: number) => {
        available_dates.push(new Date(timestamp));
      });

      updateAggregationLabels();

      showContainerLoader = false;
      buttonsDisabled = false;

      // Load a list of statistic, otherwise selector will not work
      await addStatistics();

      // Handle header depending on showHeader state
      handleHeader(showHeader, uniqueId);
    } catch (error) {
      restoreDataError = true;
      error_message = true;
      console.error("Error in restoreDataHandler: ", error);
    }
  };

  /**
   * Function for getting the aggregated data from the API and displaying it in the figure container
   */
  const getAggregatedData = async (): Promise<void> => {
    // If same dates seleted for aggregation, handle as normal date selection
    if (aggregationFrom.getTime() === aggregationTo.getTime()) {
      handleDatePickerChange(undefined, true);
      return;
    }
    previousValueAggregated = true;
    showContainerLoader = true;
    // If user selected aggregation date to be lower than base date, swap them
    if (aggregationFrom > aggregationTo) {
      [aggregationTo, aggregationFrom] = [aggregationFrom, aggregationTo];
    }
    let dateFilterList : DateFilter[] = [month_list[aggregationFrom.getMonth()], month_list[aggregationTo.getMonth()]] as DateFilter[];
    options.dateFilter = dateFilterList;

    let seriesOptions;

    if (storageData.metadata) {
      seriesOptions = selectorSelectionsToSeriesOptions(storageData.metadata[0]);
      selectorSelections = storageData.metadata[0]
    } else {
      seriesOptions = selectorSelectionsToSeriesOptions(selectorSelections);
    }

    let timestamps: Array<number> = [];
    // Create query from series options
    await Promise.all(
      seriesOptions.map(async (seriesOption) => {
        const query = createQuery(options, seriesOption);
        const data: FigureData = await dataSource.getFigureData(query, true);
        values= data.values;
        units = data.units;
        changes = data.changes;
        description = getTimeSeriesName(seriesOption, data.statName);
        timestamps = data.dates;
        if (aggregationFrom.getFullYear() !== aggregationTo.getFullYear()){
          aggregationDateTitle = `${$t(aggregationFrom.toLocaleString('en-US', { month: 'long' }))} ${aggregationFrom.getFullYear()} - ${$t(aggregationTo.toLocaleString('en-US', { month: 'long' }))} ${aggregationTo.getFullYear()}`;
        }
        else {
          aggregationDateTitle = `${$t(aggregationFrom.toLocaleString('en-US', { month: 'long' }))} - ${$t(aggregationTo.toLocaleString('en-US', { month: 'long' }))} ${aggregationTo.getFullYear()}`;;
        }

        // Change current data index to the date of the aggregation
        timestamps.forEach((date) => {
          if (aggregationFrom.getFullYear() == new Date(date).getFullYear()) {
            current_data_index = timestamps.findIndex((d) => d === date);
            }
        });
      })
    );

    // Text input for DatePicker so it shows the right selection
    text_input_date = `${aggregationFrom.getFullYear()}/${aggregationFrom.getMonth() + 1}/${aggregationFrom.getDate()}`;

    // Store dateFilter properties with index, so it can be restored
    let indexArray = ["dataIndex", current_data_index, aggregationFrom, aggregationTo]
    options.dateFilter.push(...indexArray)
    storeVisualizationData(selectorSelections)

    showContainerLoader = false;
    showDatePicker = false;
  }

  /**
   * Storing visualization data so it can be restored
   */
   const storeVisualizationData = (selectorSelections: SelectorSelections): void => {
    let storedVisualizationData: VisualizationData[] = [];
    let containerSize: string = 'h-large-v-small';
    visualizationDataStore.subscribe(
      (value) => {
        storedVisualizationData = value;
      }
    );

    const existingIndex = storedVisualizationData.findIndex((item) => item.id === uniqueId);
    if (existingIndex !== -1) {
      containerSize = storedVisualizationData[existingIndex].visualizationSize ?? '';
    };

    // Since there is only one figure displayed at a time, the metadata array will always contain only one object.
    metadata_array = [selectorSelections];
    visualizationData = {
      id: uniqueId,
      period: options.dateFilter,
      type: "Figure",
      metadata: metadata_array,
      visualizationSize: containerSize,
    };

    visualizationDataStore.updateCollection(visualizationData);
  };

  /**
   * Export handler for the share button
   * (Currently only has a placeholder console log)
   * @param type - The type of export (e.g. PNG)
   */
  const exportHandler = (type: any): void => {
    console.log("Share button clicked")
  };

  /**
   * Click handler for date-selector-button to show the date picker
   */
  const toggleDatePicker = (): void => {
    showDatePicker = !showDatePicker;
  };

  /**
   * Handler for event when date picker is closed
   */
  const closeDatePicker = (): void => {
    showDatePicker = false;
  };

  /**
   * Handler for event when aggregation picker is closed
   */
  const closeAggregationPicker = (): void => {
    aggregationPicker = false;
  };

  /**
   * Handle change in datePicker (no aggregation / aggregation with same dates)
   * @param event - event from datePicker, contains date
   * @param call_from_getAggregatedData - whether function was called from getAggregatedData function
   * (when same dates were selected in aggregation picker)
   */
  const handleDatePickerChange = async (event?: any, call_from_getAggregatedData?: boolean, closeDatePicker: boolean = true): Promise<void> => {
    if (previousValueAggregated) {
      previousValueAggregated = false;
      options.dateFilter = ["Months"];
      await restoreDataHandler(storageData, call_from_getAggregatedData);
    }
    let new_date: Date;

    // Case when the date is selected from the aggregation picker and aggregationFrom and aggregationTo are the same
    if (call_from_getAggregatedData) {
      new_date = aggregationFrom;
    }
    else {
      // Extract the date from the event
      new_date = new Date(event.detail);
    }
    let index = current_data_index;
    if (isDaily){
      // Date should exist on the available dates since the date picker is limited to available dates
      index = available_dates.findIndex((d) => d.getFullYear() === new_date.getFullYear() && d.getMonth() === new_date.getMonth() && d.getDate() === new_date.getDate());
      // Day used only for daily data
      data_day = new_date.getDate().toString();
    }
    else {
      // Date should exist on the available dates since the date picker is limited to available dates
      index = available_dates.findIndex((d) => d.getFullYear() === new_date.getFullYear() && d.getMonth() === new_date.getMonth());
    }

    // Update global variables
    data_year = new_date.getFullYear();
    data_month = $t(new_date.toLocaleString('en-US', { month: 'long' }));
    current_data_index = index

    if (latestDatapoint) {
      // If latest datapoint checked, then always load the latest datapoint
      options.dateFilter = ["Months"]
    } else {
      // Save index of selected date for restoring
      options.dateFilter = ["dataIndex", index]
    }
    storeVisualizationData(selectorSelections)

    // Update global aggregation variables
    aggregationFrom = new_date;
    aggregationTo = new_date;
    
    // Text input for DatePicker so it shows the right selection
    text_input_date = `${new_date.getFullYear()}/${new_date.getMonth() + 1}/${new_date.getDate()}`;
    showDatePicker = closeDatePicker ? false : true;
  };

  /**
   * Handle the change of the aggregation picker
   * Currently it supports only monthly aggregation, so daily is excluded
   * @param event
   */
  const handleAggregationPickerChange = (event: any): void => {
    // Extract the date from the event
    let new_date = new Date(event.detail);
    // Date should exist on the available dates since the date picker is limited to available dates
    let index = available_dates.findIndex((d) => d.getFullYear() === new_date.getFullYear() && d.getMonth() === new_date.getMonth());

    // Update global aggregation variables
    aggregationFrom = new_date;
    aggregationTo = new_date;

    // Text input for DatePicker so it shows the right selection
    text_input_date = `${new_date.getFullYear()}/${new_date.getMonth() + 1}/${new_date.getDate()}`;
    // Close the aggregation picker
    aggregationPicker = false;
  };

  /**
   * Handle the "Latest" checkmark change
   */
  const handleLatestCheckChange = () => {
    latestDatapoint = !latestDatapoint
    if (latestDatapoint) {
      const latest_date = available_dates[available_dates.length - 1]
      const event = {detail: latest_date}
      handleDatePickerChange(event, false, false)
    }
  }

  const changeAggregationTo = (direction: number): void => {
    let new_aggregateTo = new Date(aggregationTo.getFullYear(), aggregationTo.getMonth() + direction, 1)
    if (monthDiff(aggregationFrom, new_aggregateTo) <= 11
        && checkDateIncludedInArray(available_dates, new_aggregateTo)) {
      aggregationTo = new Date(aggregationTo.getFullYear(), aggregationTo.getMonth() + direction, 1);
    }
  }

  /**
   * Calculate the absolute difference in months between two dates
   * @param d1
   * @param d2
   */
  const monthDiff = (d1: Date, d2: Date) => {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return Math.abs(months);
  }

  /** Function for updating dates of aggregation */
  const updateAggregationLabels = () => {
    aggregationFrom = available_dates[current_data_index];
    aggregationTo = available_dates[current_data_index];
  }

  onMount(async () => {
    if (restoreView){
      // Set the metadata array to the metadata array from the storage.
      metadata_array = storageData.metadata;
      options.dateFilter = storageData.period;
      isDaily = storageData.metadata[0].is_daily;

      visualizationData = {
        id: storageData.id,
        type: "Figure",
        metadata: metadata_array,
        period: storageData.period,
        visualizationSize: storageData.visualizationSize,
      };

      if (storageData.period[0] == "Months" && storageData.period.length == 1){
        latestDatapoint = true;
      }

      restoreDataHandler(storageData);
    } else {
      metadata_array = [];
      await addStatistics();
    }
  });

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = (event: KeyboardEvent): void => {
    let visualisationContainer: HTMLElement = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
    let selectorButtons: NodeListOf<Element>  = visualisationContainer.querySelectorAll("#date-selector-button") as NodeListOf<Element>;
    focusedElementIndex = Array.from(selectorButtons).findIndex((element: Element) => 
      element === document.activeElement || element.contains(document.activeElement)
      );
      if (focusedElementIndex === -1) {
        focusedElementIndex = 0;
    }
    switch (event.key) {
      case "Enter":
        // Set the focus to the parent visualization container
        if (document.activeElement?.id.includes("AddData") || document.activeElement?.id.includes("back")){
          document.getElementById("container-id-" + uniqueId)?.focus();
        }
        break;
      case "ArrowDown":
        // Focus the next element in the list
        if (focusedElementIndex > 0) {
          (selectorButtons[focusedElementIndex - 1] as HTMLElement).focus();
        }
        else {
          // If the first element is focused, focus the last element
          (selectorButtons[selectorButtons.length - 1] as HTMLElement).focus();
        }
        break;
      case "ArrowUp":
        // Focus the previous element in the list
        if (focusedElementIndex < selectorButtons.length - 1) {
          (selectorButtons[focusedElementIndex + 1] as HTMLElement).focus();
        }
        else {
          // If the last element is focused, focus the first element
          (selectorButtons[0] as HTMLElement).focus();
        }
        break;
      case "ArrowLeft":
        if (document.activeElement?.id.includes("AddData") || document.activeElement?.id.includes("back")){
          // Focus to the filter select component
          let parentVisualizationContainer = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
          let filterPropsContainerList: NodeListOf<Element>  = parentVisualizationContainer.querySelectorAll('[id^="filterPropsContainer"]');
          if (filterPropsContainerList.length > 0) {
            // Set focus to the last filter select component
            (filterPropsContainerList[filterPropsContainerList.length - 1] as HTMLElement).focus();
          }
        }
        break;
      default:
        // Allow default behavior for other keys
        return;
    }
    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };
</script>

{#if !error_message}
<VisualizationContainer
  showLoader={showContainerLoader}
  selectorDisabled={buttonsDisabled}
  visualizationData={storageData}
  visualizationId={uniqueId}
  bind:this={currentContainer}
  >
  <svelte:fragment slot="buttons">
    
    <button class="date-selector-button" on:click={toggleDatePicker} id="date-selector-button" tabindex="0" on:keydown={onKeyDown}
    use:tooltip={{
      content: $t("Select Date"),
      placement: 'top',
      delay: [250, 0],
      offset: 15,
      html: true,
      target: 'div.dropdown',
    }}
    ><div class="icon"><FaRegCalendarAlt /></div></button>
    <IconDropdown
      options={Share}
      handler={exportHandler}
      title={$t("Share")}
      disabled={true}
    >
      <div class="icon" slot="icon"><FaShareAlt /></div>
    </IconDropdown>
  </svelte:fragment>
  
  <div class="selector" slot="selector">
    <Loader visible={showSelectorLoader} />

    <div class="filters">
      {#if filterSelectPropsChanged == true}
        {#each filterSelectProps as props (props.title)}
          <FilterSelect {...props} visualizationId={uniqueId}/>
        {/each}
      {:else if filterSelectPropsChanged == false}
        {#each filterSelectProps as props (props.title)}
          <FilterSelect {...props} visualizationId={uniqueId}/>
        {/each}
      {/if}
    </div>

    {#if showSelectorButtons}
      <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
      <div class="buttons" transition:fade id="figure-selector-buttons" tabindex="0" on:keydown={onKeyDown}>
        {#if filterSelectProps.length > 0}
          <Button
            handler={addFigureHandler}
            iconOnTheLeft={false}
            disabled={addDataButtonDisabled}
            id="AddData-{uniqueId}"
          >
            <FaArrowCircleRight slot="icon" />
            <svelte:fragment slot="text">{$t("Show figure")}</svelte:fragment>
          </Button>
        {/if}
        <Button handler={backHandler} iconOnTheLeft={false} id="back-{uniqueId}">
          <FaArrowCircleLeft slot="icon" />
          <svelte:fragment slot="text">{$t("Back")}</svelte:fragment>
        </Button>
      </div>
    {/if}
  </div>

  <div class="figure-container" slot="content">
    {#if values}
      <!-- Main content -->
      {#if previousValueAggregated}
        <h2 class="figure-title">{aggregationDateTitle}</h2>
      {:else}
        <h2 class="figure-title">{data_day} {data_month} {data_year}</h2>
      {/if}
      <div class="figure {changes[current_data_index] >= 0 ? (changes[current_data_index] == 0 || changes[current_data_index] == null ? 'neutral' : 'positive') : 'negative'}">
        <span class="value">{values[current_data_index] ? createValueLabel(values[current_data_index], units[current_data_index], false, false, 'abs') : $t("Loading") + "..."}</span>
        <span class="change">{"[" + (changes[current_data_index] !== undefined ? createValueLabel((changes[current_data_index] / 100), "", true, false) : (changes[current_data_index] === null ? createValueLabel(0, "", true, false) : $t("Loading") + "...")) + "]"}</span>
      </div>
      <p class="figure-description">{description}</p>
      <p class="figure-description" id="figure-source">{`${$t("Sources")}: ${source_text}`}</p>

      <!-- Date selector -->
      {#if showDatePicker}
        <div class="date-selector {aggregationMode ? 'aggregation-mode' : ''}">
          {#if isDaily}
            <input class="date-input" type="text" bind:value={text_input_date}/>
            <DatePicker available_dates={available_dates} value={text_input_date} isDaily={isDaily} applyButtonDisabled={latestDatapoint} on:dateChanged={handleDatePickerChange} on:closeDatePicker={closeDatePicker}/>
          {:else}
            {#if !aggregationPicker}
            <div class="row">
              <label for="aggregationMode" class="checkbox-label">
                <input class="check-input-box" id="aggregationMode" type="checkbox" bind:checked={aggregationMode} disabled={isDaily} />
                <span class="custom-checkbox"></span>
                {$t("Aggregation")}
              </label>
              <!-- Checkmark with reactive variable will result in endless loop, so checkmark is wrapped in button-->
              <button on:click|preventDefault={() => {handleLatestCheckChange()}}>
                <label for="latestCheck" class="checkbox-label">
                  <input class="check-input-box" id="latestCheck" type="checkbox" bind:checked={latestDatapoint} />
                  <span class="custom-checkbox"></span>
                  {$t("Latest")}
                </label>
              </button>
              <div class="help-icon"
              use:tooltip={{
                content: $t('<h3>Latest</h3><p>When checked, just press "Close"-button and the latest figure will be loaded on your every visit until you change it. "Apply"-button will then be disabled.</p><p>When unchecked, you can select any available date, press the "Apply"-button and a figure of that date will be loaded.</p>'),
                placement: 'top',
                delay: [250, 0],
                offset: 15,
                html: true,
                target: 'div.dropdown',
              }}>
                <FaRegQuestionCircle />
              </div>
            </div>
            {/if}
            {#if aggregationMode && !aggregationPicker}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div class="row" style="gap: 2rem;">
                <div class="col">
                  <h4 style="margin-bottom: 0;">Month</h4>
                  <div class="date-display" on:click={() => {aggregationPicker = !aggregationPicker}}>{`${aggregationFrom.getMonth() + 1}/${aggregationFrom.getFullYear()}`}</div>
                </div>
                <div class="col">
                  <h4 style="margin-bottom: 0;">Aggregate to</h4>
                  <div class="row">
                    <!-- Button for changing aggrigation date down -->
                    <div class="icon {(monthDiff(aggregationFrom, aggregationTo) == 11 && aggregationTo < aggregationFrom || !checkDateIncludedInArray(available_dates, new Date(aggregationTo.getFullYear(), aggregationTo.getMonth() - 1, 1))) ? 'disabled' : ''}" on:click={() => changeAggregationTo(-1)}><FaAngleDown /></div>
                    <!-- Container for showing aggregation date -->
                    <div class="aggregation-display">{`${aggregationTo.getMonth() + 1}/${aggregationTo.getFullYear()}`}</div>
                    <!-- Button for changing aggrigation date up -->
                    <div class="icon {(monthDiff(aggregationFrom, aggregationTo) == 11 && aggregationTo > aggregationFrom || !checkDateIncludedInArray(available_dates, new Date(aggregationTo.getFullYear(), aggregationTo.getMonth() + 1, 1))) ? 'disabled' : ''}" on:click={() => changeAggregationTo(1)}><FaAngleUp /></div>
                  </div>
                </div>
              </div>
              <div class="button-wrapper">
                <Button handler={() => {getAggregatedData()}} iconOnTheLeft={false} title={$t("Aggregate")} disabled={false} id="figureGetAggrigatedData">
                  <FaArrowCircleRight slot="icon" />
                  <svelte:fragment slot="text">{$t("Aggregate")}</svelte:fragment>
                </Button>
                <Button handler={closeDatePicker} iconOnTheLeft={false} title={$t("Cancel")} id="figureCloseDatePicker">
                  <FaRegTimesCircle slot="icon" />
                  <svelte:fragment slot="text">{$t("Cancel")}</svelte:fragment>
                </Button>
              </div>
            {:else if aggregationMode && aggregationPicker}
              <DatePicker available_dates={available_dates} value={text_input_date} aggregationMode={true} isDaily={isDaily} on:dateChanged={handleAggregationPickerChange} on:closeDatePicker={closeAggregationPicker}/>
            {:else}
              <DatePicker available_dates={available_dates} value={text_input_date} applyButtonDisabled={latestDatapoint} isDaily={isDaily} on:dateChanged={handleDatePickerChange} on:closeDatePicker={closeDatePicker}/>
            {/if}
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</VisualizationContainer>
{/if}

<!-- Showing an error message when data is unavailable. -->
{#if error_message}
  <CustomError visualizationId={uniqueId} showAdditionalInfo={restoreDataError}>
    <p slot="additional-information" class="additional-error-info">{$t("This error might dissappear on page reload")}</p>
  </CustomError>
{/if}

<style lang="scss">
  .selector {
    display: flex;
    gap: 1rem;
    height: 100%;

    .filters {
      display: flex;
      gap: 0.5rem;
    }

    .buttons {
      display: flex;
      flex-direction: column;
      justify-content: stretch;
      gap: 1rem;

      :global(button:first-child) {
        flex: 1;
        background-color: var(--color-primary);
      }

      :global(button:last-child) {
        flex: 1;
        background-color: var(--color-tertiary);
      }
    }

    :global(.loader-container) {
      background-color: #232124;
    }
  }

  .figure-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;

    .figure-title {
      font-size: calc(25px + 0.3vw);
      color: var(--color-secondary);
      margin-bottom: 0;
      margin-top: 0;
    }

    .figure {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 1vw;

      padding: 1rem 2rem;
      border: 4px solid var(--color-tertiary);
      border-radius: 0.5rem;

      font-size: calc(26px + 0.8vw);
      font-weight: bold;
      color: var(--color-tertiary);
      overflow-wrap: anywhere;

      .change {
        font-size: calc(20px + 0.6vw);
      }

      &.positive {
        border-color: var(--color-primary);
        color: var(--color-primary);
      }

      &.negative {
        border-color: var(--color-tertiary);
        color: var(--color-tertiary);
      }

      &.neutral {
        border-color: rgb(190, 190, 0);
        color: rgb(190, 190, 0);
      }
    }

    .figure-description {
      font-size: calc(10px + 0.3vw);
      text-align: center;
      color: var(--color-secondary);
      margin-bottom: 0;
      margin-top: 10px; 
    }
  }

  @media (max-width: 768px) {
    .figure-container {
      .figure {
        padding: 1rem;
        font-size: calc(30px + 1.5vw);

        .change {
          font-size: calc(25px + 0.5vw);
        }
      }
      .figure-description {
        font-size: calc(10px + 1vw);
      }
    }
  }

  .date-selector-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    color: var(--color-gray);

    &:hover {
      cursor: pointer;
    }
  }

  .date-selector {
    position: absolute;
    z-index: 9;
    background-color: var(--color-white);
    padding: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
    border: 2px solid var(--color-primary);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-height: 17.2rem;

    &.aggregation-mode {
      padding: 1.5rem 3rem;
    }

    .col {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .row {
      display: flex;
      flex-direction: row;
      gap: 1rem;
    }

    .date-display {
      width: 70px;
      padding: 0.5rem;
      text-align: center;
      border-radius: 0.25rem;
      background-color: rgb(230, 230, 230);
      font-size: large;
      user-select: none;
      // Change pointer style
      &:hover {
        cursor: pointer;
      }
    }

    .aggregation-display {
      padding: 0.5rem;
      text-align: center;
      border-radius: 0.25rem;
      background-color: rgb(230, 230, 230);
      font-size: large;
      user-select: none;
      width: 70px;
    }

    .icon {
      height: 100%;

      &.disabled {
        color: var(--color-gray);
        pointer-events: none;
      }
    }
    
    .button-wrapper {
      margin-top: 2rem;
      display: flex;
      flex-direction: row;
      gap: 0.5rem;
    }
  }

  .date-input {
    width: 60%;
    text-align: center;
    border: 1px solid var(--color-primary);
  }


  .check-input-box {
    display: none; /* Hide the native checkbox */
  }

  .custom-checkbox {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: #fff;
    border: 2px solid #000;
    cursor: pointer;
  }

  .custom-checkbox::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 10px;
    border: solid var(--color-black);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    top: 0px;
    left: 4px;
    display: none;
  }

  .check-input-box:checked + .custom-checkbox::after {
    display: block;
  }

  .check-input-box:disabled + .custom-checkbox {
    background-color: #ddd;
    cursor: not-allowed;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    margin-bottom: 0.5rem;
    user-select: none;
  }

  .help-icon {
    width: 20px;
    height: 20px;
    color: var(--color-gray);
  }

  .additional-error-info {
    margin: 0 0 30px 0;
  }

</style>
